import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Landmark, Globe, GraduationCap, TrendingUp, Award } from "lucide-react";
import TriCTA from "../components/TriCTA";

const PILLARS = [
  { icon: Landmark, title: "SEZA Industrial Licensing", body: "Project sits inside the Special Economic Zones Authority framework. Fast-track approvals, tax incentives, export-driven mandate." },
  { icon: ShieldCheck, title: "Land Allocation at Scale", body: "35 hectares allocated within the Lobatse Special Economic Zone. Title transfer in process. Master planning underway." },
  { icon: Globe, title: "Export Manufacturing Mandate", body: "Built explicitly for export to EU, US and global luxury markets. Aligned with national diversification strategy." },
  { icon: TrendingUp, title: "Job Creation & Localisation", body: "800 direct jobs · 2,000 indirect. Botswana-first hiring with Italian-led training programmes." },
  { icon: GraduationCap, title: "Skills Transfer", body: "Italian master tanners and craftspeople deliver structured training. Local capacity built to international standard." },
  { icon: Award, title: "Import Substitution & Value Add", body: "Hides processed onshore. Finished goods produced onshore. Value captured onshore. Not exported as raw material." },
];

export default function Seza() {
  return (
    <div data-testid="page-seza" className="bg-[#F9F9F6]">
      <section className="pt-32 md:pt-40 pb-16 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow">Government & SEZA</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight mt-6">
              Aligned with the <span className="italic text-[#5C2018]">national strategy.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#1A1A1A] max-w-2xl leading-relaxed">
              This is not a private speculation. The Botswana Leather Project is structured around SEZA licensing, industrial land allocation, export manufacturing, job creation, skills transfer and Botswana value-addition. All anchored to government priorities.
            </p>
          </div>
        </div>
      </section>

      {/* SEZA INVESTOR LICENCE — GRANTED */}
      <section data-testid="seza-licence-granted" className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-16">
        <div className="border border-[#1A1A1A] bg-white grid grid-cols-12 gap-0">
          {/* Left: seal area */}
          <div className="col-span-12 md:col-span-4 bg-[#1A1A1A] text-[#F9F9F6] p-8 md:p-10 lg:p-12 flex flex-col items-center justify-center text-center">
            <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-[#E5C4A1]" />
              <div className="absolute inset-3 rounded-full border border-[#E5C4A1]/50" />
              <div className="relative text-center">
                <p className="font-serif text-[#E5C4A1] text-xl leading-none">SEZA</p>
                <p className="text-[8px] uppercase tracking-[0.3em] text-[#E5C4A1] mt-2">Granted</p>
                <p className="text-[8px] uppercase tracking-[0.22em] text-[#A8A8A0] mt-2">16 · 03 · 2026</p>
              </div>
            </div>
            <p className="font-serif text-base mt-8 tracking-tight">Special Economic Zones Authority</p>
            <p className="eyebrow text-[#A8A8A0] mt-2">Republic of Botswana</p>
          </div>

          {/* Right: certificate details */}
          <div className="col-span-12 md:col-span-8 p-8 md:p-10 lg:p-12">
            <p className="eyebrow text-[#5C2018]">Regulatory Status · Granted</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight mt-4 leading-tight">
              SEZA Investor Licence
              <br />
              <span className="italic text-[#5C2018]">issued and active.</span>
            </h2>
            <p className="mt-5 text-base text-[#1A1A1A] leading-relaxed max-w-2xl">
              The Special Economic Zones Authority Board has granted LRG Holdings (Pty) Ltd a Special Economic Zone licence for the purpose of setting up a leather processing plant and tannery at Lobatse. The licence is active and subject to the terms and conditions of the issuing authority.
            </p>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[#E5E5E0] pt-6">
              {[
                ["Licence Number", "SEZBW00029"],
                ["Date of Licensing", "16 March 2026"],
                ["Validity", "50 Years"],
                ["Location", "Lobatse"],
              ].map(([k, v]) => (
                <div key={k} data-testid={`licence-detail-${k.toLowerCase().replace(/\s/g, "-")}`}>
                  <p className="eyebrow">{k}</p>
                  <p className="font-serif text-lg md:text-xl mt-2 tracking-tight">{v}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-[#E5E5E0] pt-6">
              <div>
                <p className="eyebrow">Issued To</p>
                <p className="font-serif text-base md:text-lg mt-2 tracking-tight">
                  LRG Holdings Proprietary Limited
                  <br />
                  <span className="text-sm text-[#666666]">Plot 6815, Kgope Close, Extension 21, Broadhurst, Gaborone</span>
                </p>
              </div>
              <div>
                <p className="eyebrow">Purpose of Licence</p>
                <p className="font-serif text-base md:text-lg mt-2 tracking-tight">
                  Setting up a leather processing plant and tannery
                </p>
              </div>
            </div>

            <p className="mt-8 text-xs text-[#666666] inline-flex items-center gap-2">
              <span className="h-px w-8 bg-[#666666]" />
              Signed: Ms. Rorisang Modikana, Chairperson, SEZA Board · Date issued: 26 May 2026
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-24" data-testid="seza-pillars">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#1A1A1A]">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="border-r border-b border-[#1A1A1A] p-8 md:p-10 hover:bg-white transition-colors">
                <div className="w-10 h-10 border border-[#1A1A1A] flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <h3 className="font-serif text-2xl tracking-tight mt-6 leading-tight">{p.title}</h3>
                <p className="text-sm text-[#1A1A1A] mt-3 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Quote / authority strip */}
      <section className="bg-[#5C2018] text-[#F9F9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow text-[#E5C4A1]">Authority Positioning</p>
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight mt-5">
              "LRG Holdings is not only presenting a project. LRG Holdings is building the execution platform that brings land, licensing, finance, partners, manufacturing, training and export markets together. In one system."
            </p>
            <p className="mt-6 text-sm text-[#E0D8CD]">. LRG Holdings, Project Charter</p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/execution" data-testid="seza-to-execution" className="inline-flex items-center gap-3 px-7 py-4 bg-[#F9F9F6] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] hover:text-[#F9F9F6]">
              Execution Structure <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Ecosystem alignment */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24" data-testid="seza-ecosystem">
        <div className="grid grid-cols-12 gap-8 mb-10">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow">Ecosystem</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 leading-tight">Anchored, by design.</h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-sm text-[#1A1A1A] leading-relaxed max-w-xl">
              Each anchor below represents a government, regulatory or strategic pillar that pre-de-risks the project for institutional capital.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
          {[
            ["SEZA", "Special Economic Zones Authority. Licensing & incentives"],
            ["BMC", "Botswana Meat Commission. Hide supply pipeline"],
            ["Italian Industry", "Tannery technology + luxury manufacturing partners"],
            ["Local Training Institutions", "Skills transfer & technical curriculum"],
            ["Export Trade Frameworks", "EU/US preferential access for finished goods"],
            ["LRG Holdings", "Project lead, SPV holder, execution coordinator"],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#F9F9F6] p-6 md:p-8">
              <p className="eyebrow text-[#5C2018]">{k}</p>
              <p className="font-serif text-xl md:text-2xl tracking-tight mt-3 leading-tight">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Institutional Framework — 6 Botswana institutions */}
      <section className="bg-[#F9F9F6] border-t border-[#E5E5E0]" data-testid="seza-institutional-framework">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow">Institutional Framework</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 leading-tight">
                Aligned with Botswana&rsquo;s development framework.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-sm text-[#1A1A1A] leading-relaxed">
                Six institutions form the policy and finance backbone of the Botswana Leather Park. Each anchors a different dimension of the platform.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#1A1A1A]">
            {[
              ["SEZA", "Special Economic Zones Authority", "Regulatory framework, investment incentives, and industrial zone infrastructure"],
              ["ETP", "Economic Transformation Programme", "National strategy for economic diversification and industrial development"],
              ["CEDA", "Citizen Entrepreneurial Development Agency", "Enterprise development support and citizen economic participation"],
              ["NDP", "National Development Plan", "Long-term national planning framework for sustainable growth"],
              ["BDC", "Botswana Development Corporation", "Development finance and project structuring support"],
              ["NDB", "National Development Bank Botswana", "Industrial financing and capital access for strategic projects"],
            ].map(([code, name, body]) => (
              <div key={code} data-testid={`framework-${code.toLowerCase()}`} className="border-r border-b border-[#1A1A1A] p-8 md:p-10 bg-[#F9F9F6] hover:bg-white transition-colors flex flex-col">
                <span className="font-serif text-[#5C2018] text-sm tracking-[0.05em] border border-[#5C2018] px-3 py-1 self-start">
                  {code}
                </span>
                <h3 className="font-serif text-xl md:text-2xl tracking-tight mt-8 leading-tight">{name}</h3>
                <p className="text-sm text-[#1A1A1A] mt-4 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Macro credibility strip */}
      <section className="bg-[#1A1A1A] text-[#F9F9F6]" data-testid="seza-macro-stats">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
          <p className="eyebrow text-[#A8A8A0] mb-8">National Context</p>
          <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#333330]">
            {[
              ["~1.7M", "National Herd"],
              ["BBB− / Baa1", "Sovereign Credit (IG)"],
              ["55+", "Years of Stability"],
              ["5%", "SEZ Tax (yrs 1-10)"],
            ].map(([v, k]) => (
              <div key={k} className="border-r border-b border-[#333330] p-6 md:p-8">
                <p className="font-serif text-4xl md:text-5xl text-[#E5C4A1] tracking-tight">{v}</p>
                <p className="eyebrow text-[#A8A8A0] mt-3">{k}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance & Compliance — 8 cards */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32" data-testid="seza-governance">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">Institutional Standards</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 leading-tight">
              Governance &amp; compliance.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-sm text-[#1A1A1A] leading-relaxed">
              Institutional investors, SEZA and development finance institutions require demonstrable governance and compliance frameworks. The platform is designed to meet international standards from inception.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1A1A1A]">
          {[
            ["Regulatory Compliance", "Full alignment with SEZA regulations, Botswana industrial policy and SEZ operational requirements"],
            ["Environmental Standards", "ZDHC chemistry protocol, CETP effluent treatment, ISO 14064 carbon reporting, water re-use systems"],
            ["Corporate Governance", "Independent board oversight, transparent financial reporting, anti-corruption compliance, audit readiness"],
            ["Export Compliance", "AGOA, EU-SADC EPA and UK-SACUM qualification standards, rules of origin documentation, customs compliance"],
            ["Worker Safety", "Occupational health and safety management, chemical handling protocols, emergency response systems"],
            ["Responsible Sourcing", "BMC-verified hide traceability, ethical procurement, animal welfare alignment, supply chain documentation"],
            ["Traceability Systems", "Hide-batch traceability from BMC to finished article for luxury buyer and ESG reporting requirements"],
            ["ESG Reporting", "Higg FEM alignment, transparent emissions baseline, development impact metrics, social return tracking"],
          ].map(([title, body], i) => (
            <div key={title} data-testid={`gov-${i}`} className="border-r border-b border-[#1A1A1A] p-6 md:p-8 bg-[#F9F9F6] hover:bg-white transition-colors">
              <span className="font-serif text-2xl text-[#5C2018]">0{i + 1}</span>
              <h3 className="font-serif text-xl md:text-2xl tracking-tight mt-6 leading-tight">{title}</h3>
              <p className="text-sm text-[#1A1A1A] mt-3 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <TriCTA kicker="Engage" heading={<>Three ways to plug into the <span className="italic text-[#E5C4A1]">national project.</span></>} />
    </div>
  );
}
